import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useActorDetailsQuery } from '@mwxltd/flix-api-client'
import { AdPlayer } from '@flix/common/domain/ads/AdPlayer/AdPlayer'
import {
  DesktopVideoControls,
  FeaturedActorDetails,
  MobileVideoControls,
  PlayerControls,
  VideoPlaylistPlayer,
} from '@flix/common/domain/video'
import { useDimensions } from '@flix/common/elements/layout'
import { isMobileDevice } from '@flix/common/style/browserDetect'
import { useMediaBreakpoints } from '@flix/common/style/media'
import Error404 from '../../../containers/errors/Error404/Error404'
import VideoErrorBoundary from './VideoErrorBoundary'

const VideoPlayer = ({
  errorMetadata,
  onRequestClose,
  bigPlayButton,
  featuredActorsEnabled = true,
  nextRecommendation,
  contentType,
  ...playerProps
}) => {
  const history = useHistory()
  const { url } = useRouteMatch()
  const dimensions = useDimensions()
  const { md: isDesktop } = useMediaBreakpoints('sm')

  // Since larger iPad dimensions appear to be for desktop browser
  // detection is used in addition to a media query
  const isMobile = isMobileDevice || !isDesktop || dimensions.isIPadSize
  const VideoControls = isMobile ? MobileVideoControls : DesktopVideoControls
  const shownControlsDuration = isMobile ? 5000 : 2000

  return (
    <VideoErrorBoundary onBack={onRequestClose} metadata={errorMetadata}>
      {({ onVideoError, notifyError }) => (
        <VideoPlaylistPlayer
          {...dimensions}
          {...playerProps}
          shownControlsHeight={VideoControls.controlsHeight}
          shownControlsDuration={shownControlsDuration}
          onRequestClose={onRequestClose}
          notifyError={notifyError}
          onError={onVideoError}
        >
          {(videoControlsProps) => (
            <Switch>
              <Route
                path={`${url}/actor/:actorSlug`}
                render={({ history, match }) => (
                  <PlayerControls.Backdrop visible opacity={0.6}>
                    <FeaturedActor
                      slug={match.params.actorSlug}
                      onBack={history.goBack}
                      desktopBtns={!isMobile}
                    />
                  </PlayerControls.Backdrop>
                )}
              />

              <Route
                render={() => (
                  <>
                    <VideoControls
                      {...videoControlsProps}
                      contentType={contentType}
                      nextRecommendation={nextRecommendation}
                      onActorSelect={
                        featuredActorsEnabled &&
                        ((actorSlug) => history.push(`${url}/actor/${actorSlug}`))
                      }
                      bigPlayButton={bigPlayButton}
                    />
                    {videoControlsProps.advert && (
                      <AdPlayer
                        {...dimensions}
                        onPlayFeature={videoControlsProps.onPlay}
                        onPauseFeature={videoControlsProps.onPause}
                        advert={videoControlsProps.advert}
                        onRequestCloseAdvert={videoControlsProps.onRequestCloseAdvert}
                        shownControlsHeight={VideoControls.controlsHeight}
                        shownControlsDuration={shownControlsDuration}
                        onRequestClose={onRequestClose}
                        notifyError={notifyError}
                        onError={onVideoError}
                        parentFullscreen={videoControlsProps.fullscreen}
                      >
                        {(adControlsProps) => (
                          <VideoControls
                            {...adControlsProps}
                            onFullscreenPress={videoControlsProps.onFullscreenPress}
                            scrubbingDisabled
                            progressTrackColor="accent"
                          />
                        )}
                      </AdPlayer>
                    )}
                  </>
                )}
              />
            </Switch>
          )}
        </VideoPlaylistPlayer>
      )}
    </VideoErrorBoundary>
  )
}

const FeaturedActor = ({ slug, ...props }) => {
  const { loading, error, data } = useActorDetailsQuery({ slug })
  if (loading) return null
  const actor = data?.actor
  if (!actor || error) return <Error404 item="actor" />
  return <FeaturedActorDetails {...props} actor={actor} />
}

VideoPlayer.propTypes = {
  playlist: PropTypes.array.isRequired,
  onNextVideo: PropTypes.func,
  onRequestRefreshUrl: PropTypes.func,
  onRequestClose: PropTypes.func,
  errorMetaData: PropTypes.object,
  userActiveOnKeydown: PropTypes.bool,
  playbackStatusOverride: PropTypes.shape({
    playing: PropTypes.bool.isRequired,
    seekTime: PropTypes.number.isRequired,
  }),
  onPlaybackStatusChange: PropTypes.func,
  onPlaySuccess: PropTypes.func,
  groupWatchChatEnabled: PropTypes.bool,
  featuredActorsEnabled: PropTypes.bool,
  videoHidden: PropTypes.bool,
  filmSlug: PropTypes.string,
  festivalId: PropTypes.string,
  nextRecommended: PropTypes.object,
}

export default VideoPlayer
